import { useState, useEffect } from 'react';
import { Star, Plus, User } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useServices } from '../context/ServicesContext';
import { Toaster, toast } from 'sonner';

import { API } from '../config/api';

function StarRating({ rating, interactive, onChange }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type={interactive ? 'button' : undefined}
          onClick={interactive ? () => onChange(star) : undefined}
          className={interactive ? 'cursor-pointer' : 'cursor-default'}
          data-testid={`star-${star}`}
        >
          <Star
            className={`w-5 h-5 ${
              star <= rating ? 'fill-amber-400 text-amber-400' : 'fill-gray-200 text-gray-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function Reviews() {
  const { services } = useServices();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', rating: 5, text: '', service: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews`);
      setReviews(res.data);
    } catch (e) {
      console.error('Failed to fetch reviews', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text) {
      toast.error('Please fill in your name and review.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        name: form.name,
        rating: form.rating,
        text: form.text,
        service: form.service || null,
      });
      toast.success('Review added successfully!');
      setForm({ name: '', rating: 5, text: '', service: '' });
      setDialogOpen(false);
      fetchReviews();
    } catch (e) {
      toast.error('Failed to add review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="reviews-page">
      <Toaster position="top-right" richColors />

      {/* Hero */}
      <section className="bg-sky-50 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700 mb-3">Testimonials</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-black text-slate-900 mb-4">
            Customer Reviews
          </h1>
          <p className="text-slate-500 text-base max-w-lg mx-auto mb-6">
            See what our customers are saying about Top Tier Washing.
          </p>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-review-btn"
                className="bg-sky-700 text-white font-semibold rounded-full px-6 py-3 hover:bg-sky-600"
              >
                <Plus className="w-4 h-4 mr-2" /> Add a Review
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" data-testid="add-review-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Leave a Review</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Your Name</label>
                  <Input
                    data-testid="review-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="John D."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Rating</label>
                  <StarRating rating={form.rating} interactive onChange={(r) => setForm({ ...form, rating: r })} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Service (optional)</label>
                  <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                    <SelectTrigger data-testid="review-service-select">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((s) => (
                        <SelectItem key={s.slug} value={s.name}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Your Review</label>
                  <Textarea
                    data-testid="review-text-input"
                    value={form.text}
                    onChange={(e) => setForm({ ...form, text: e.target.value })}
                    placeholder="Tell us about your experience..."
                    rows={4}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-review-btn"
                  className="w-full bg-sky-700 text-white rounded-full hover:bg-sky-600"
                >
                  {submitting ? 'Submitting...' : 'Submit Review'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {/* Reviews list */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-400">Loading reviews...</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-16" data-testid="no-reviews">
              <User className="w-16 h-16 text-sky-200 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">No Reviews Yet</h3>
              <p className="text-slate-400">Be the first to leave a review!</p>
            </div>
          ) : (
            <div className="space-y-6" data-testid="reviews-list">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  data-testid={`review-card-${review.id}`}
                  className="bg-white rounded-2xl border border-sky-100 p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-sky-600" />
                      </div>
                      <div>
                        <p className="font-heading font-semibold text-slate-900">{review.name}</p>
                        {review.service && (
                          <p className="text-xs text-slate-400">{review.service}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <StarRating rating={review.rating} />
                  <p className="text-slate-600 text-sm leading-relaxed mt-3">{review.text}</p>
                  <p className="text-slate-300 text-xs mt-3">
                    {new Date(review.created_at).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    })}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
